import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import StatusBadge from '../../components/StatusBadge';

const fmt = (d) => (d ? new Date(d).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const toDateInput = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

const TERM_FIELDS = [
  { key: 'positionTitle', label: 'Position title' },
  { key: 'anzscoCode', label: 'ANZSCO code' },
  { key: 'anzscoTitle', label: 'ANZSCO title' },
  { key: 'employmentType', label: 'Employment type', options: ['full-time', 'part-time', 'casual'] },
  { key: 'commencementDate', label: 'Commencement date', type: 'date' },
  { key: 'primaryWorksite', label: 'Primary worksite (name and full address)', wide: true },
  { key: 'ordinaryHoursPerWeek', label: 'Ordinary hours per week', type: 'number' },
  { key: 'salaryAnnual', label: 'Annual base salary (AUD)' },
  { key: 'salaryHourly', label: 'Hourly rate (AUD, blank = worked out)' },
  { key: 'classificationLevel', label: 'Award Direct Care level' },
  { key: 'probationMonths', label: 'Probation (months)', type: 'number' },
];

const Section = ({ title, children, action }) => (
  <section className="wc-card p-5">
    <div className="mb-3 flex items-center justify-between">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-primary">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const Row = ({ label, children }) => (
  <div className="flex flex-wrap gap-x-4 border-b border-line py-2 text-sm last:border-0">
    <dt className="w-44 shrink-0 text-muted">{label}</dt>
    <dd className="flex-1 break-words">{children || '—'}</dd>
  </div>
);

const ContractDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [editing, setEditing] = useState(false);
  const [terms, setTerms] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api(`/admin/contracts/${id}`, { auth: true });
      setData(res.data);
      const t = res.data.contract.terms || {};
      setTerms(Object.fromEntries(TERM_FIELDS.map((f) => [f.key, f.type === 'date' ? toDateInput(t[f.key]) : (t[f.key] ?? '')])));
    } catch (err) {
      setError(err.message);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, done) => {
    setBusy(true);
    setError('');
    setFlash('');
    try { await fn(); if (done) setFlash(done); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const saveTerms = () => act(async () => {
    const body = {};
    TERM_FIELDS.forEach((f) => {
      const v = terms[f.key];
      body[f.key] = v === '' ? '' : f.type === 'number' ? Number(v) : v;
    });
    await api(`/admin/contracts/${id}/terms`, { method: 'POST', auth: true, body });
    setEditing(false);
    await load();
  }, 'Terms saved.');

  const openPreview = () => act(async () => {
    const res = await api(`/admin/contracts/${id}/preview`, { auth: true, raw: true });
    const url = URL.createObjectURL(await res.blob());
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  });

  const downloadSigned = () => act(async () => {
    const res = await api(`/admin/contracts/${id}/pdf`, { auth: true });
    window.location.href = res.data.url;
  });

  const resend = () => act(async () => {
    await api(`/admin/contracts/${id}/resend-signed`, { method: 'POST', auth: true });
    await load();
  }, 'Sent to Ivipass again.');

  if (!data) return error ? <p className="rounded-lg bg-danger-soft p-3 text-sm text-danger">{error}</p> : <p className="text-muted">Loading…</p>;

  const c = data.contract;
  const s = c.signing || {};

  return (
    <div className="space-y-5">
      <Link to="/staff/contracts" className="text-sm text-primary hover:underline">← All contracts</Link>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-bold">{(c.candidate && c.candidate.fullName) || c.reference}</h1>
        <StatusBadge status={c.status} />
      </div>
      {flash && <p className="rounded-lg bg-success-soft p-3 text-sm text-success">{flash}</p>}
      {error && <p className="rounded-lg bg-danger-soft p-3 text-sm text-danger">{error}</p>}

      {c.status === 'issued' && data.missing.length > 0 && (
        <div className="rounded-xl border border-danger/30 bg-danger-soft p-4 text-sm">
          <p className="font-semibold text-danger">The candidate can't sign yet. Missing:</p>
          <ul className="mt-2 ml-5 list-disc text-ink">
            {data.missing.map((m) => <li key={m.field}>{m.label}{m.field.startsWith('employer.') && <> (<Link className="text-primary underline" to="/staff/settings">settings</Link>)</>}</li>)}
          </ul>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Candidate">
          <dl>
            <Row label="Name">{c.candidate && c.candidate.fullName}</Row>
            <Row label="Email">{c.candidate && c.candidate.email}</Row>
            <Row label="Phone">{c.candidate && c.candidate.phone}</Row>
            <Row label="Country">{c.candidate && c.candidate.country}</Row>
            <Row label="Reference"><code>{c.reference}</code></Row>
            <Row label="Issued">{fmt(c.issuedAt)}</Row>
            <Row label="First opened">{c.firstViewedAt ? `${fmt(c.firstViewedAt)} (${c.viewCount} view${c.viewCount === 1 ? '' : 's'})` : 'Not yet'}</Row>
            {c.status === 'withdrawn' && <Row label="Withdrawn">{`${fmt(c.withdrawnAt)} — ${c.withdrawnReason || 'no reason given'}`}</Row>}
          </dl>
        </Section>

        <Section
          title="Terms"
          action={c.status === 'issued' && !editing && <button type="button" className="text-sm text-primary underline" onClick={() => setEditing(true)}>Edit</button>}
        >
          {!editing ? (
            <dl>
              {TERM_FIELDS.map((f) => (
                <Row key={f.key} label={f.label}>{f.type === 'date' ? (c.terms[f.key] && new Date(c.terms[f.key]).toLocaleDateString('en-AU')) : c.terms[f.key]}</Row>
              ))}
            </dl>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {TERM_FIELDS.map((f) => (
                <div key={f.key} className={f.wide ? 'sm:col-span-2' : ''}>
                  <label className="wc-label" htmlFor={f.key}>{f.label}</label>
                  {f.options ? (
                    <select id={f.key} className="wc-input" value={terms[f.key]} onChange={(e) => setTerms({ ...terms, [f.key]: e.target.value })}>
                      <option value="">—</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input id={f.key} type={f.type || 'text'} className="wc-input" value={terms[f.key]} onChange={(e) => setTerms({ ...terms, [f.key]: e.target.value })} />
                  )}
                </div>
              ))}
              <div className="flex gap-2 sm:col-span-2">
                <button type="button" className="wc-btn-primary" disabled={busy} onClick={saveTerms}>Save terms</button>
                <button type="button" className="wc-btn-ghost" onClick={() => { setEditing(false); load(); }}>Cancel</button>
              </div>
              <p className="text-xs text-muted sm:col-span-2">Terms normally come from the employer record in Ivipass. Changes here apply to this contract only, and are kept in its history.</p>
            </div>
          )}
        </Section>
      </div>

      <Section title="Document">
        <div className="flex flex-wrap gap-2">
          {c.status === 'issued' && <button type="button" className="wc-btn-ghost" disabled={busy} onClick={openPreview}>Preview unsigned PDF</button>}
          {c.status === 'signed' && <button type="button" className="wc-btn-primary" disabled={busy} onClick={downloadSigned}>Download signed PDF</button>}
          {c.status === 'signed' && <button type="button" className="wc-btn-ghost" disabled={busy} onClick={resend}>Send to Ivipass again</button>}
        </div>
      </Section>

      {c.status === 'signed' && (
        <Section title="Signature evidence">
          <dl>
            <Row label="Signed at">{fmt(s.signedAt)}</Row>
            <Row label="Name signed">{s.signedName}</Row>
            <Row label="Address">{s.address}</Row>
            <Row label="ID number">{s.idNumber}</Row>
            <Row label="Signature">{s.signatureMode}</Row>
            <Row label="IP address">{s.ip}</Row>
            <Row label="Browser">{s.userAgent}</Row>
            <Row label="Contract version">{s.templateVersion}</Row>
            <Row label="PDF SHA-256"><code className="text-xs">{c.pdf && c.pdf.sha256}</code></Row>
            <Row label="Copy emailed">{c.candidateEmail && c.candidateEmail.sentAt ? fmt(c.candidateEmail.sentAt) : (c.candidateEmail && c.candidateEmail.error) || 'No'}</Row>
            <Row label="Consent given">{s.consentText}</Row>
          </dl>
        </Section>
      )}

      <Section title="Messages with Ivipass">
        <table className="w-full text-left text-sm">
          <thead className="text-xs uppercase tracking-wide text-muted">
            <tr><th className="py-2">Event</th><th className="py-2">Direction</th><th className="py-2">When</th><th className="py-2">Result</th></tr>
          </thead>
          <tbody>
            {data.webhooks.incoming.map((w) => (
              <tr key={w._id} className="border-t border-line"><td className="py-2">{w.type}</td><td>from Ivipass</td><td>{fmt(w.receivedAt || w.createdAt)}</td><td><StatusBadge status="delivered" /></td></tr>
            ))}
            {data.webhooks.outgoing.map((w) => (
              <tr key={w._id} className="border-t border-line"><td className="py-2">{w.type}</td><td>to Ivipass</td><td>{fmt(w.createdAt)}</td><td><StatusBadge status={w.status} />{w.lastError && <span className="ml-2 text-xs text-muted">{w.lastError}</span>}</td></tr>
            ))}
            {data.webhooks.incoming.length + data.webhooks.outgoing.length === 0 && (
              <tr><td colSpan={4} className="py-3 text-muted">None.</td></tr>
            )}
          </tbody>
        </table>
      </Section>
    </div>
  );
};

export default ContractDetail;
